import React, { useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, FilterIcon, PlusIcon, BellIcon, DownloadIcon, ListIcon, ClockIcon, MailIcon, ExternalLinkIcon } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
const PolicyCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 8, 1)); // September 2025
  const [view, setView] = useState<'month' | 'agenda' | 'reminders'>('month');
  const [filterOpen, setFilterOpen] = useState(false);
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  // Helper function to get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };
  // Helper function to get first day of month (0 = Sunday, 6 = Saturday)
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };
  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({
        day: null,
        events: []
      });
    }
    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      // Find events for this day
      const dayEvents = calendarEvents.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate.getDate() === day && eventDate.getMonth() === month && eventDate.getFullYear() === year;
      });
      days.push({
        day,
        events: dayEvents
      });
    }
    return days;
  };
  // Sample calendar events
  const calendarEvents = [{
    id: 1,
    title: 'UnitedHealthcare Policy Review',
    date: '2025-09-15',
    type: 'review',
    impact: 'high',
    payer: 'UnitedHealthcare',
    description: 'Quarterly review of Peripheral Nerve Stimulation Coverage Policy'
  }, {
    id: 2,
    title: 'Aetna Policy Effective Date',
    date: '2025-09-01',
    type: 'effective',
    impact: 'high',
    payer: 'Aetna',
    description: 'New policy for Electrical Stimulation for Pain becomes effective'
  }, {
    id: 3,
    title: 'Cigna Policy Comment Period Ends',
    date: '2025-09-22',
    type: 'deadline',
    impact: 'medium',
    payer: 'Cigna',
    description: 'Last day to submit comments on draft Neurostimulation policy'
  }, {
    id: 4,
    title: 'BCBS Michigan Policy Update',
    date: '2025-10-08',
    type: 'update',
    impact: 'medium',
    payer: 'BCBS Michigan',
    description: 'Updates to Peripheral Nerve Field Stimulation policy'
  }, {
    id: 5,
    title: 'Humana Policy Announcement',
    date: '2025-10-05',
    type: 'announcement',
    impact: 'low',
    payer: 'Humana',
    description: 'New policy announcement for Pain Management Devices'
  }, {
    id: 6,
    title: 'Anthem Policy Reconsideration Meeting',
    date: '2025-10-17',
    type: 'meeting',
    impact: 'high',
    payer: 'Anthem',
    description: 'Meeting to discuss reconsideration of PNS policy'
  }, {
    id: 7,
    title: 'Kaiser Permanente Policy Effective Date',
    date: '2025-11-30',
    type: 'effective',
    impact: 'medium',
    payer: 'Kaiser Permanente',
    description: 'Updated Neurostimulation for Chronic Pain policy takes effect'
  }, {
    id: 8,
    title: 'BCBS Florida Draft Policy Release',
    date: '2025-11-12',
    type: 'draft',
    impact: 'medium',
    payer: 'BCBS Florida',
    description: 'Draft policy release for Peripheral Nerve Stimulation'
  }, {
    id: 9,
    title: 'Medicare Annual Policy Update',
    date: '2025-12-01',
    type: 'update',
    impact: 'high',
    payer: 'Medicare',
    description: 'Annual update to Medicare coverage policies for neurostimulation devices'
  }, {
    id: 10,
    title: 'Cigna Implementation Deadline',
    date: '2025-12-15',
    type: 'deadline',
    impact: 'high',
    payer: 'Cigna',
    description: 'Implementation deadline for updated prior authorization requirements'
  }, {
    id: 11,
    title: 'BCBS Association Policy Summit',
    date: '2025-12-08',
    type: 'meeting',
    impact: 'medium',
    payer: 'BCBS Association',
    description: 'Annual policy summit to discuss coverage standardization across BCBS entities'
  }, {
    id: 12,
    title: 'Aetna Q1 2026 Policy Preview',
    date: '2025-12-20',
    type: 'announcement',
    impact: 'medium',
    payer: 'Aetna',
    description: 'Preview of policy changes planned for Q1 2026'
  }, {
    id: 13,
    title: 'UnitedHealthcare Policy Effective Date',
    date: '2026-01-01',
    type: 'effective',
    impact: 'high',
    payer: 'UnitedHealthcare',
    description: 'New policy for minimally invasive pain treatments becomes effective'
  }, {
    id: 14,
    title: 'Humana Coverage Review Meeting',
    date: '2026-01-10',
    type: 'review',
    impact: 'medium',
    payer: 'Humana',
    description: 'Quarterly review of coverage policies for pain management technologies'
  }, {
    id: 15,
    title: 'Medicaid State Policy Alignment Deadline',
    date: '2026-01-15',
    type: 'deadline',
    impact: 'high',
    payer: 'Medicaid',
    description: 'Deadline for state Medicaid programs to align with new federal guidance'
  }, {
    id: 16,
    title: 'BCBS California Draft Policy Release',
    date: '2026-01-22',
    type: 'draft',
    impact: 'medium',
    payer: 'BCBS California',
    description: 'Draft policy release for updated neurostimulation coverage'
  }];
  // Get all events for the current month for agenda view
  const getMonthEvents = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    return calendarEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getMonth() === month && eventDate.getFullYear() === year;
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };
  // Get upcoming reminders (next 14 days)
  const getUpcomingReminders = () => {
    const today = new Date();
    const twoWeeksLater = new Date();
    twoWeeksLater.setDate(today.getDate() + 14);
    return calendarEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= today && eventDate <= twoWeeksLater;
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(0, 5); // Get top 5 upcoming events
  };
  // Get event type color
  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'review':
        return 'bg-blue-100 text-blue-800';
      case 'effective':
        return 'bg-green-100 text-green-800';
      case 'deadline':
        return 'bg-red-100 text-red-800';
      case 'update':
        return 'bg-purple-100 text-purple-800';
      case 'announcement':
        return 'bg-gray-100 text-gray-800';
      case 'meeting':
        return 'bg-amber-100 text-amber-800';
      case 'draft':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const calendarDays = generateCalendarDays();
  const monthEvents = getMonthEvents();
  const upcomingReminders = getUpcomingReminders();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Handle export to calendar functions
  const exportToIcal = () => {
    alert('Exporting to iCal...');
    // In a real implementation, this would generate an .ics file
  };
  const exportToOutlook = () => {
    alert('Exporting to Outlook...');
    // In a real implementation, this would use Outlook's web protocol
  };
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-slate-50" data-id="element-1450">
      <div className="mb-6" data-id="element-1451">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-1452">
          <div data-id="element-1453">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1454">
              Policy Calendar
            </h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1455">
              Track upcoming policy reviews, effective dates, and important
              deadlines
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3" data-id="element-1456">
            <div className="p-1 bg-white rounded-lg shadow flex" data-id="element-1457">
              <button className={`px-3 py-1.5 text-sm font-medium rounded flex items-center ${view === 'month' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setView('month')} data-id="element-1458">
                <CalendarIcon className="w-4 h-4 mr-1" data-id="element-1459" />
                Month
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded flex items-center ${view === 'agenda' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setView('agenda')} data-id="element-1460">
                <ListIcon className="w-4 h-4 mr-1" data-id="element-1461" />
                Agenda
              </button>
              <button className={`px-3 py-1.5 text-sm font-medium rounded flex items-center ${view === 'reminders' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setView('reminders')} data-id="element-1462">
                <ClockIcon className="w-4 h-4 mr-1" data-id="element-1463" />
                Reminders
              </button>
            </div>
            <button className="p-2 text-gray-500 bg-white rounded-full shadow hover:text-gray-700" onClick={() => setFilterOpen(!filterOpen)} data-id="element-1464">
              <FilterIcon className="w-5 h-5" data-id="element-1465" />
            </button>
            <button className="p-2 text-gray-500 bg-white rounded-full shadow hover:text-gray-700" data-id="element-1466">
              <PlusIcon className="w-5 h-5" data-id="element-1467" />
            </button>
            <button className="p-2 text-gray-500 bg-white rounded-full shadow hover:text-gray-700" data-id="element-1468">
              <DownloadIcon className="w-5 h-5" data-id="element-1469" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Header */}
      <div className="bg-white rounded-t-lg shadow p-4 flex items-center justify-between" data-id="element-1470">
        <div className="flex items-center" data-id="element-1471">
          <button onClick={prevMonth} className="p-2 rounded-full hover:bg-gray-100" data-id="element-1472">
            <ChevronLeftIcon className="w-5 h-5 text-gray-600" data-id="element-1473" />
          </button>
          <h2 className="text-xl font-semibold text-gray-900 mx-4" data-id="element-1474">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h2>
          <button onClick={nextMonth} className="p-2 rounded-full hover:bg-gray-100" data-id="element-1475">
            <ChevronRightIcon className="w-5 h-5 text-gray-600" data-id="element-1476" />
          </button>
          <button className="ml-4 px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100" data-id="element-1477">
            Today
          </button>
        </div>
        <div className="hidden md:flex items-center space-x-2" data-id="element-1478">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1479">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-1" data-id="element-1480"></span>
            Review
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1481">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-1" data-id="element-1482"></span>
            Effective
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1483">
            <span className="w-2 h-2 bg-red-500 rounded-full mr-1" data-id="element-1484"></span>
            Deadline
          </span>
        </div>
      </div>

      {/* Filter Panel */}
      {filterOpen && <div className="bg-white border-t border-b border-gray-200 p-4 grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1485">
          <div data-id="element-1486">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1487">
              Event Type
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1488">
              <option data-id="element-1489">All Types</option>
              <option data-id="element-1490">Review</option>
              <option data-id="element-1491">Effective Date</option>
              <option data-id="element-1492">Deadline</option>
              <option data-id="element-1493">Update</option>
              <option data-id="element-1494">Announcement</option>
              <option data-id="element-1495">Meeting</option>
              <option data-id="element-1496">Draft</option>
            </select>
          </div>
          <div data-id="element-1497">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1498">
              Payer
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1499">
              <option data-id="element-1500">All Payers</option>
              <option data-id="element-1501">UnitedHealthcare</option>
              <option data-id="element-1502">Aetna</option>
              <option data-id="element-1503">Cigna</option>
              <option data-id="element-1504">Blue Cross Blue Shield</option>
              <option data-id="element-1505">Humana</option>
              <option data-id="element-1506">Anthem</option>
            </select>
          </div>
          <div data-id="element-1507">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1508">
              Impact Level
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1509">
              <option data-id="element-1510">All Levels</option>
              <option data-id="element-1511">High</option>
              <option data-id="element-1512">Medium</option>
              <option data-id="element-1513">Low</option>
            </select>
          </div>
          <div data-id="element-1514">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1515">
              Date Range
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1516">
              <option data-id="element-1517">Current Month</option>
              <option data-id="element-1518">Next 3 Months</option>
              <option data-id="element-1519">Next 6 Months</option>
              <option data-id="element-1520">Custom Range</option>
            </select>
          </div>
        </div>}

      <div className="grid grid-cols-1 gap-6" data-id="element-1521">
        {/* Main Calendar Content */}
        <div className="col-span-1" data-id="element-1522">
          {/* Month View */}
          {view === 'month' && <div className="bg-white rounded-b-lg shadow overflow-hidden" data-id="element-1523">
              {/* Days of week header */}
              <div className="grid grid-cols-7 gap-px bg-gray-200" data-id="element-1524">
                {dayNames.map((day, index) => <div key={index} className="bg-gray-50 py-2 text-center text-sm font-medium text-gray-700" data-id="element-1525">
                    {day}
                  </div>)}
              </div>
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-px bg-gray-200" data-id="element-1526">
                {calendarDays.map((dayData, index) => <div key={index} className={`bg-white min-h-[120px] p-2 ${dayData.day ? '' : 'bg-gray-50'}`} data-id="element-1527">
                    {dayData.day && <>
                        <div className="text-right" data-id="element-1528">
                          <span className={`text-sm ${new Date().getDate() === dayData.day && new Date().getMonth() === currentMonth.getMonth() && new Date().getFullYear() === currentMonth.getFullYear() ? 'bg-blue-600 text-white h-6 w-6 rounded-full flex items-center justify-center ml-auto' : 'text-gray-700'}`} data-id="element-1529">
                            {dayData.day}
                          </span>
                        </div>
                        <div className="mt-1 space-y-1 max-h-[90px] overflow-y-auto" data-id="element-1530">
                          {dayData.events.map(event => <div key={event.id} className={`px-2 py-1 text-xs rounded truncate ${getEventTypeColor(event.type)}`} title={event.title} data-id="element-1531">
                              {event.title}
                            </div>)}
                        </div>
                      </>}
                  </div>)}
              </div>
            </div>}

          {/* Agenda View */}
          {view === 'agenda' && <div className="bg-white rounded-b-lg shadow" data-id="element-1532">
              <div className="overflow-hidden" data-id="element-1533">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1534">
                  <thead className="bg-gray-50" data-id="element-1535">
                    <tr data-id="element-1536">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1537">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1538">
                        Event
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1539">
                        Payer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1540">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1541">
                        Impact
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1542">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1543">
                    {monthEvents.length > 0 ? monthEvents.map(event => {
                  const eventDate = new Date(event.date);
                  const formattedDate = eventDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  return <tr key={event.id} className="hover:bg-gray-50" data-id="element-1544">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1545">
                              {formattedDate}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1546">
                              <div className="font-medium" data-id="element-1547">{event.title}</div>
                              <div className="text-gray-500 text-xs mt-1" data-id="element-1548">
                                {event.description}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600" data-id="element-1549">
                              {event.payer}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-1550">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEventTypeColor(event.type)}`} data-id="element-1551">
                                {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-1552">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${event.impact === 'high' ? 'bg-red-100 text-red-800' : event.impact === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-1553">
                                {event.impact.charAt(0).toUpperCase() + event.impact.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1554">
                              <button className="text-blue-600 hover:text-blue-900" data-id="element-1555">
                                View
                              </button>
                            </td>
                          </tr>;
                }) : <tr data-id="element-1556">
                        <td colSpan={6} className="px-6 py-10 text-center text-gray-500" data-id="element-1557">
                          No events scheduled for this month
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>}

          {/* Reminders View */}
          {view === 'reminders' && <div className="bg-white rounded-b-lg shadow p-6" data-id="element-1558">
              <div className="flex justify-between items-center mb-6" data-id="element-1559">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1560">
                  All Reminders
                </h3>
                <div className="flex space-x-2" data-id="element-1561">
                  <button onClick={exportToIcal} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1562">
                    <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1563" />
                    Export to iCal
                  </button>
                  <button onClick={exportToOutlook} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1564">
                    <MailIcon className="h-4 w-4 mr-2" data-id="element-1565" />
                    Export to Outlook
                  </button>
                </div>
              </div>
              <div className="overflow-hidden" data-id="element-1566">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1567">
                  <thead className="bg-gray-50" data-id="element-1568">
                    <tr data-id="element-1569">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1570">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1571">
                        Event
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1572">
                        Payer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1573">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1574">
                        Impact
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1575">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1576">
                    {calendarEvents.map(event => {
                  const eventDate = new Date(event.date);
                  const formattedDate = eventDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  // Calculate days from now
                  const today = new Date();
                  const diffTime = Math.abs(eventDate.getTime() - today.getTime());
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  const isPast = eventDate < today;
                  return <tr key={event.id} className="hover:bg-gray-50" data-id="element-1577">
                          <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-1578">
                            <div className="text-gray-900" data-id="element-1579">{formattedDate}</div>
                            <div className={`text-xs ${isPast ? 'text-red-500' : 'text-gray-500'}`} data-id="element-1580">
                              {isPast ? `${diffDays} days ago` : `in ${diffDays} days`}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1581">
                            <div className="font-medium" data-id="element-1582">{event.title}</div>
                            <div className="text-gray-500 text-xs mt-1" data-id="element-1583">
                              {event.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600" data-id="element-1584">
                            {event.payer}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-1585">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEventTypeColor(event.type)}`} data-id="element-1586">
                              {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-1587">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${event.impact === 'high' ? 'bg-red-100 text-red-800' : event.impact === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-1588">
                              {event.impact.charAt(0).toUpperCase() + event.impact.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-1589">
                            <div className="flex space-x-2" data-id="element-1590">
                              <button className="text-blue-600 hover:text-blue-900" data-id="element-1591">
                                View
                              </button>
                              <button className="text-blue-600 hover:text-blue-900" data-id="element-1592">
                                Set Reminder
                              </button>
                            </div>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
export default PolicyCalendar;